import { useEffect, useId, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/Button';

import { ORIENTATION } from '../catalogue';
import type { GuidanceInternals } from '../context';
import { useAnyGuidanceDirty } from '../dirty';
import { fixedBottomInset } from '../engine/dom';
import { waitForTarget } from '../engine/targets';
import type { AnchorId } from '../anchors';
import { isTourAvailable, orientationSteps } from '../eligibility';
import { useGuidanceState } from '../store';
import type { GuidePageId } from '../safeRoutes';
import type { Portal } from '../types';

/**
 * The first-visit invitation (brief §7): a small, dismissible, non-blocking
 * card offering the portal's orientation guide.
 *
 * It appears only inside a signed-in, MFA-complete shell (that is where
 * `GuidanceRoot` lives), only on the portal's landing page, only once that
 * page's primary content is on screen — one of its landing anchors resolves,
 * within the engine's bounded wait; never over a skeleton, and not at all on a
 * visit where it does not arrive — never while a form holds unsaved input, never
 * while a guide or the Help panel is open — and only until it is dismissed or
 * the orientation has been started. It never starts anything by itself, and it
 * never takes focus (a blur on the cart saves), so it is not announced as an
 * alert either: it is a labelled region a person reaches in reading order.
 *
 * Only where progress can be KEPT: without browser storage it would come back
 * on every visit, which is nagging, not an invitation.
 *
 * "Show me around" is the one-minute orientation (TOUR-02): the guide's steps
 * that need no record, ending with Finish on the portal's own pages — never a
 * pause waiting for a record the person has not opened. The full guide, record
 * steps included, is Help & tours' to start or continue.
 */
const LANDING: Record<Portal, GuidePageId> = {
  tenant: 'tenant.dashboard',
  vendor: 'vendor.home',
  platform: 'platform.overview',
};

/** The landing page's primary content: rendered only once its data has loaded. */
const LANDING_CONTENT: Record<Portal, readonly AnchorId[]> = {
  tenant: ['tenant.dashboard.your-work', 'tenant.dashboard.figures'],
  vendor: ['vendor.home.work'],
  platform: ['platform.overview.metrics'],
};

export function FirstVisitInvitation({ guidance }: { guidance: GuidanceInternals }) {
  const { t } = useTranslation();
  const headingId = useId();
  const { panelOpen, active } = useGuidanceState(guidance.store);
  const stored = useSyncExternalStore(
    guidance.progress.subscribe,
    guidance.progress.get,
    guidance.progress.get,
  );
  const dirty = useAnyGuidanceDirty();
  const bottom = useBottomInset();

  const { ctx, catalogue, page, portal, progress, store } = guidance;
  const def = catalogue.find((tour) => tour.id === ORIENTATION[portal]);
  const onLanding = page?.id === LANDING[portal];
  const ready = useLandingContent(onLanding ? LANDING_CONTENT[portal] : null, ctx.pathname);

  if (
    def === undefined ||
    progress.isMemoryOnly() ||
    stored.invitation === 'dismissed' ||
    progress.status(def) !== 'not_started' ||
    !onLanding ||
    !ready ||
    panelOpen ||
    active !== null ||
    dirty ||
    !isTourAvailable(def, ctx)
  )
    return null;

  const first = orientationSteps(def, ctx).at(0);

  return (
    <section
      aria-labelledby={headingId}
      style={{ bottom: bottom + 16 }}
      className="fixed end-4 z-20 w-[min(22rem,calc(100vw-2rem))] rounded-(--radius-card) border border-line-subtle bg-card p-4 shadow-(--shadow-lg)"
    >
      <h2 id={headingId} className="type-card-title text-ink-heading">
        {t('guidance.invitation.title')}
      </h2>
      <p className="type-meta mt-1 text-ink-muted">{t('guidance.invitation.body')}</p>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => progress.dismissInvitation()}>
          {t('guidance.invitation.dismiss')}
        </Button>
        {first !== undefined ? (
          <Button
            size="sm"
            onClick={() => {
              progress.dismissInvitation();
              store.start({ tourId: def.id, mode: 'orientation', stepId: first.id });
            }}
          >
            {t('guidance.invitation.start')}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/** Above the fixed bars along the bottom edge (the phone's navigation), kept current on resize. */
function useBottomInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const measure = () => setInset(fixedBottomInset());

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, []);

  return inset;
}

/**
 * Whether the landing page's primary content is on screen: one of its anchors
 * resolves within the engine's bounded wait (never more than four seconds).
 * Asked once per visit to the page, and then kept — a background refetch
 * that briefly shows a skeleton does not make the invitation flicker. A visit
 * where the content never arrives shows no invitation.
 */
function useLandingContent(anchors: readonly AnchorId[] | null, pathname: string): boolean {
  const [ready, setReady] = useState<{ at: string; found: boolean } | null>(null);

  useEffect(() => {
    if (anchors === null) return;

    const controller = new AbortController();

    waitForTarget(anchors, { signal: controller.signal })
      .then((result) => setReady({ at: pathname, found: result.status === 'found' }))
      .catch(() => undefined);

    return () => controller.abort();
  }, [anchors, pathname]);

  return anchors !== null && ready !== null && ready.at === pathname && ready.found;
}
